import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import {
	DISPLAY_MAX_LTV,
	getRiskZone,
	ltvGradient,
	riskZoneColor,
	RISK_THRESHOLDS,
	type RiskZone,
} from "@/lib/defi-math";
import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "framer-motion";

const ZONE_LABELS: Record<RiskZone, string> = {
	safe: "Healthy",
	moderate: "Caution",
	danger: "At risk",
	liquidatable: "Liquidatable",
	underwater: "Underwater",
};

/**
 * Full-width LTV track always; fill width = progress.
 * Outer track is never overflow-clipped so fill glow stays visible.
 */
export function AnimatedLTVBar({
	ltv,
	maxLtv = DISPLAY_MAX_LTV,
	className,
	showLabels = true,
	healthFactor,
	variant = "default",
}: {
	ltv: number;
	maxLtv?: number;
	className?: string;
	showLabels?: boolean;
	healthFactor?: number;
	variant?: "default" | "compact";
}) {
	const reducedMotion = useReducedMotion();
	const isCompact = variant === "compact";
	const clamped = Math.min(100, Math.max(0, ltv));
	const fillPct = Math.min(100, Math.max(0, (clamped / maxLtv) * 100));
	const zone = getRiskZone(clamped);
	const color = riskZoneColor(zone);
	const safeEndPct = Math.min(100, (RISK_THRESHOLDS.safe / maxLtv) * 100);
	const moderateEndPct = Math.min(
		100,
		(RISK_THRESHOLDS.moderate / maxLtv) * 100,
	);
	const hfDisplay =
		healthFactor != null && Number.isFinite(healthFactor)
			? healthFactor.toFixed(2)
			: healthFactor === Number.POSITIVE_INFINITY
				? "∞"
				: null;

	return (
		<div className={cn(isCompact ? "space-y-1" : "space-y-2", className)}>
			{showLabels && (
				<div className="flex items-center justify-between text-xs">
					<span className="font-mono-amount font-semibold">
						LTV <AnimatedNumber value={clamped} decimals={1} suffix="%" />
					</span>
					{hfDisplay != null && (
						<span className="text-muted-foreground">
							HF{" "}
							<span
								className="font-mono-amount font-semibold"
								style={{ color }}
							>
								{hfDisplay}×
							</span>
						</span>
					)}
				</div>
			)}

			{/* Full-width rail — overflow visible so fill shadow isn't clipped */}
			<div
				className={cn(
					"relative w-full rounded-full",
					isCompact ? "h-2.5 py-0.5" : "h-4 py-0.5",
				)}
			>
				{/* Track background (always 100% width) */}
				<div
					className={cn(
						"absolute inset-x-0 top-1/2 w-full -translate-y-1/2 rounded-full bg-muted ring-1 ring-border/50",
						isCompact ? "h-2" : "h-3",
					)}
				/>
				{/* Zone tints across full track */}
				<div
					className={cn(
						"pointer-events-none absolute inset-x-0 top-1/2 flex w-full -translate-y-1/2 overflow-hidden rounded-full",
						isCompact ? "h-2" : "h-3",
					)}
				>
					<div
						className="h-full bg-emerald-500/20"
						style={{ width: `${safeEndPct}%` }}
					/>
					<div
						className="h-full bg-amber-500/20"
						style={{ width: `${Math.max(0, moderateEndPct - safeEndPct)}%` }}
					/>
					<div
						className="h-full flex-1 bg-red-500/15"
						style={{
							width: `${Math.max(0, 100 - moderateEndPct)}%`,
						}}
					/>
				</div>
				{/* Progress fill — width only; glow outside clip */}
				{fillPct > 0 ? (
					<motion.div
						className={cn(
							"absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full",
							isCompact ? "h-2" : "h-3",
						)}
						style={{
							background: ltvGradient(clamped, maxLtv),
							boxShadow: `0 0 10px ${color}66, 0 1px 2px ${color}44`,
						}}
						initial={false}
						animate={{ width: `${fillPct}%` }}
						transition={
							reducedMotion
								? { duration: 0 }
								: { type: "spring", stiffness: 90, damping: 20 }
						}
					/>
				) : null}
			</div>

			{showLabels && !isCompact && (
				<div className="flex justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
					<span className="text-emerald-600">Safe</span>
					<span style={{ color }}>{ZONE_LABELS[zone]}</span>
					<span className="text-red-600">Max {maxLtv.toFixed(0)}%</span>
				</div>
			)}
		</div>
	);
}
